import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import { toaster } from '@/shared/libs';
import { ConfirmDialog } from '@/shared/ui';
import {
  getUserDisplayName,
  ROLE_OPTIONS_EXTERNAL,
  ROLE_OPTIONS_INTERNAL,
  useAssignUserRole,
  useChangeUserRole,
  useSetUserStatusMutation,
} from '@entities/User';
import type { User, UserRole } from '@psp-aggregator/sdk';

import { InviteUserModal } from '../InviteUserModal';
import { RejectUserModal } from '../RejectUserModal';
import { AssignRoleModal, ChangeRoleModal } from '../RoleModals';
import { UserDetailsModal } from '../UserDetailsModal/UserDetailsModal';

export interface UsersManagementModalsProps {
  assignTarget: User | null;
  changeTarget: User | null;
  confirmBlock: User | null;
  confirmUnblock: User | null;
  detailUser: User | null;
  inviteOpen: boolean;
  onAssignClose: () => void;
  onChangeClose: () => void;
  onConfirmBlockClose: () => void;
  onConfirmUnblockClose: () => void;
  onDetailClose: () => void;
  onInviteClose: () => void;
  onRejectClose: () => void;
  rejectTarget: User | null;
}

export const UsersManagementModals: FC<UsersManagementModalsProps> = ({
  assignTarget,
  changeTarget,
  confirmBlock,
  confirmUnblock,
  detailUser,
  inviteOpen,
  onAssignClose,
  onChangeClose,
  onConfirmBlockClose,
  onConfirmUnblockClose,
  onDetailClose,
  onInviteClose,
  onRejectClose,
  rejectTarget,
}) => {
  const { t } = useTranslation();
  const assignRole = useAssignUserRole();
  const changeRole = useChangeUserRole();
  const setStatus = useSetUserStatusMutation();
  const submitting =
    assignRole.isPending || changeRole.isPending || setStatus.isPending;

  const notifyError = (fallback: string, error: unknown): void => {
    toaster.error({
      title: isAxiosError(error)
        ? (error.response?.data?.message ?? fallback)
        : t('errors.unknown'),
    });
  };

  const isInternalRole = (role: UserRole): boolean =>
    (ROLE_OPTIONS_INTERNAL as readonly UserRole[]).includes(role);

  return (
    <>
      {!!assignTarget && (
        <AssignRoleModal
          submitting={submitting}
          user={assignTarget}
          onClose={onAssignClose}
          onConfirm={async (role) => {
            try {
              await assignRole.mutateAsync({
                role,
                userId: assignTarget.id,
              });
              toaster.success({ title: t('toast.roleAssigned') });
              onAssignClose();
            } catch (error) {
              notifyError(t('errors.users.assignFailed'), error);
            }
          }}
        />
      )}

      {!!changeTarget?.role && (
        <ChangeRoleModal
          currentRole={changeTarget.role}
          options={
            isInternalRole(changeTarget.role)
              ? ROLE_OPTIONS_INTERNAL
              : ROLE_OPTIONS_EXTERNAL
          }
          submitting={submitting}
          user={changeTarget}
          onClose={onChangeClose}
          onConfirm={async (role) => {
            try {
              await changeRole.mutateAsync({
                role,
                userId: changeTarget.id,
              });
              toaster.success({ title: t('toast.roleChanged') });
              onChangeClose();
            } catch (error) {
              notifyError(t('errors.users.changeRoleFailed'), error);
            }
          }}
        />
      )}

      {!!rejectTarget && (
        <RejectUserModal
          submitting={submitting}
          user={rejectTarget}
          onClose={onRejectClose}
          onConfirm={async (reason) => {
            try {
              await setStatus.mutateAsync({
                reason: reason || undefined,
                status: 'rejected',
                userId: rejectTarget.id,
              });
              toaster.success({ title: t('toast.userRejected') });
              onRejectClose();
            } catch (error) {
              notifyError(t('errors.users.rejectFailed'), error);
            }
          }}
        />
      )}

      <ConfirmDialog
        busy={submitting}
        cancelLabel={t('buttons.cancel')}
        confirmLabel={t('buttons.block')}
        description={
          confirmBlock
            ? t('pages.admin.users.modals.blockDescription', {
                email: confirmBlock.email,
                name: getUserDisplayName(confirmBlock),
              })
            : undefined
        }
        open={Boolean(confirmBlock)}
        title={t('pages.admin.users.modals.blockTitle')}
        tone="danger"
        onCancel={onConfirmBlockClose}
        onConfirm={() => {
          if (!confirmBlock) {
            return;
          }

          void (async () => {
            try {
              await setStatus.mutateAsync({
                status: 'blocked',
                userId: confirmBlock.id,
              });
              toaster.success({ title: t('toast.userBlocked') });
              onConfirmBlockClose();
            } catch (error) {
              notifyError(t('errors.users.blockFailed'), error);
            }
          })();
        }}
      />

      <ConfirmDialog
        busy={submitting}
        cancelLabel={t('buttons.cancel')}
        confirmLabel={t('buttons.unblock')}
        description={
          confirmUnblock
            ? t('pages.admin.users.modals.unblockDescription', {
                email: confirmUnblock.email,
                name: getUserDisplayName(confirmUnblock),
              })
            : undefined
        }
        open={Boolean(confirmUnblock)}
        title={t('pages.admin.users.modals.unblockTitle')}
        tone="success"
        onCancel={onConfirmUnblockClose}
        onConfirm={() => {
          if (!confirmUnblock) {
            return;
          }

          void (async () => {
            try {
              await setStatus.mutateAsync({
                status: 'active',
                userId: confirmUnblock.id,
              });
              toaster.success({ title: t('toast.userUnblocked') });
              onConfirmUnblockClose();
            } catch (error) {
              notifyError(t('errors.users.unblockFailed'), error);
            }
          })();
        }}
      />

      {inviteOpen && <InviteUserModal onClose={onInviteClose} />}

      {!!detailUser && (
        <UserDetailsModal user={detailUser} onClose={onDetailClose} />
      )}
    </>
  );
};

UsersManagementModals.displayName = 'UsersManagementModals';
